/**
 * Lightweight locale system for the web app.
 * Locale is derived from salon.country — no URL-based routing needed.
 */

export type WebLocale = "en" | "ja";

/** Map a salon's country code to a display locale. */
export function localeFromCountry(country?: string | null): WebLocale {
  switch (country?.toUpperCase()) {
    case "JP":
      return "ja";
    default:
      return "en";
  }
}

const dict = {
  en: {
    // Modal / dialog
    appointment: "Appointment",

    // booking.error.network
    bookingNetworkError: "We couldn't complete your request. Try again.",

    // Phone lookup
    phoneNumber: "Phone Number",
    smsConsent:
      "I agree to receive SMS from Openings about my appointments (confirmations, reminders, changes). Msg & data rates may apply. Reply STOP to opt out.",
    smsConsentRequired: "Please confirm SMS consent to continue.",
    otpHint: "We'll text a one-time code to verify this number.",

    // New customer form
    verificationSentMessage:
      "We've sent a verification code to your mobile number. Please fill out the form with the code to continue.",
    firstName: "First Name",
    lastName: "Last Name",
    verificationCode: "Verification Code",
    codePlaceholder: "Code",
    submit: "Submit",
    resend: "Re-send",
    resendCooldown: "Sent · Re-send in 1 min",
    // booking.verification.alreadySent: a code is still usable, so this is
    // recovery copy, not an error.
    verificationAlreadySent:
      "A code was already sent. Enter it below or wait to request another.",

    // Email verification
    verificationEmailPrompt: "Enter your email to receive a verification code.",
    verificationEmailSentMessage:
      "A verification code has been sent to your email.",
    email: "Email",
    emailPlaceholder: "you@example.com",
    sendCode: "Send Code",

    // Repeat client verification
    verifyPhoneMessage:
      "We need to verify your phone number before continuing. Please enter the code sent to your mobile device.",

    // Confirmation
    youreBooked: "You're booked!",
    appointmentConfirmed: "Your appointment has been confirmed.",
    note: "Appointment note",
    leaveNote: "Write a note",
    addDetails: "Add details or preferences for your provider...",
    saveNote: "Save note",
    noteSaved: "Note saved",
    noteSaveFailed: "We couldn't save your note. Please try again.",
    details: "Details",
    at: "At",
    address: "Address",
    provider: "Provider",
    // booking.review.memberNotes / booking.confirmation.memberNotes
    memberNotes: "Before your visit",
    services: "Services",
    description: "Description",
    saveToCalendar: "Save to your calendar",
    done: "Done",

    // Reschedule
    existingAppointmentsGeneric:
      "Looks like you already have an upcoming appointment! Would you like to schedule a new one, or perhaps reschedule an existing one?",
    makeAnother: "Make another appointment",
    reschedule: "Reschedule",
    submittingAppointment:
      "Your appointment is being submitted. Please hold on a moment...",
    selectToReschedule:
      "Please click on the original appointment that you would like to reschedule.",
    reschedulingFrom: (from: string, to: string) =>
      `We're updating your appointment date from ${from} to ${to}`,
    confirm: "Confirm",
    back: "Back",
    processingReschedule:
      "Your appointment rescheduling request is being processed. Please hold on a moment...",
    reschedulePrepaymentRequired:
      "Rescheduling this appointment needs payment upfront. Contact the business to reschedule it.",

    // booking.payment.pendingTitle / pendingBody / pendingChargedBody: the
    // charge may have reached the processor, so the flow stops there.
    paymentPendingTitle: "We're confirming your payment",
    paymentPendingBody:
      "We couldn't confirm your payment yet. Please don't pay again. The business will confirm your appointment.",
    paymentPendingChargedBody:
      "Your payment went through, but we couldn't finish your booking. Please don't pay again. The business will confirm your appointment.",
    close: "Close",
    // booking.error.bookingInProgress / bookingMayExist: a reused create key.
    bookingInProgress:
      "We're still working on your booking. Please wait a moment and don't pay again.",
    bookingMayExist:
      "We may already have your booking. Please don't pay again. Check your texts or email for a confirmation, or contact the business.",

    // Booking section
    sendRequest: "Send a Request",
    allBooked: "All Booked",
    nServices: (n: number) => `${n} services`,
  },
  ja: {
    // booking.error.network
    bookingNetworkError: "リクエストを完了できませんでした。もう一度お試しください。",
    appointment: "予約",

    phoneNumber: "電話番号",
    smsConsent:
      "Openingsからの予約に関するSMS（確認、リマインダー、変更）の受信に同意します。通信料が発生する場合があります。配信停止はSTOPと返信してください。",
    smsConsentRequired: "続行するにはSMS同意を確認してください。",
    otpHint: "この番号を確認するため、ワンタイムコードをSMSで送信します。",

    verificationSentMessage:
      "携帯電話に認証コードを送信しました。コードを入力して続けてください。",
    firstName: "名",
    lastName: "姓",
    verificationCode: "認証コード",
    codePlaceholder: "コード",
    submit: "送信",
    resend: "再送信",
    resendCooldown: "送信済み · 1分後に再送信",
    verificationAlreadySent:
      "認証コードはすでに送信されています。下に入力するか、しばらく待ってから再送信してください。",

    verificationEmailPrompt:
      "メールアドレスを入力して認証コードを受け取ってください。",
    verificationEmailSentMessage: "認証コードをメールに送信しました。",
    email: "メールアドレス",
    emailPlaceholder: "you@example.com",
    sendCode: "コードを送信",

    verifyPhoneMessage:
      "続行するには電話番号の確認が必要です。携帯端末に送信されたコードを入力してください。",

    youreBooked: "予約完了！",
    appointmentConfirmed: "ご予約が確認されました。",
    note: "予約メモ",
    leaveNote: "メモを書く",
    addDetails: "担当者への詳細やご要望をご記入ください...",
    saveNote: "メモを保存",
    noteSaved: "メモを保存しました",
    noteSaveFailed:
      "メモを保存できませんでした。もう一度お試しください。",
    details: "詳細",
    at: "日時",
    address: "住所",
    provider: "担当者",
    memberNotes: "ご来店の前に",
    services: "サービス",
    description: "内容",
    saveToCalendar: "カレンダーに保存",
    done: "完了",

    existingAppointmentsGeneric:
      "既にご予約があります。新しい予約を作成しますか、それとも既存の予約を変更しますか？",
    makeAnother: "新しい予約を作成",
    reschedule: "予約変更",
    submittingAppointment: "予約を送信中です。少々お待ちください...",
    selectToReschedule: "変更したい予約をクリックしてください。",
    reschedulingFrom: (from: string, to: string) =>
      `予約日を${from}から${to}に変更します`,
    confirm: "確認",
    back: "戻る",
    processingReschedule: "予約変更を処理中です。少々お待ちください...",
    reschedulePrepaymentRequired:
      "この予約の変更には事前のお支払いが必要です。予約の変更はお店にお問い合わせください。",

    paymentPendingTitle: "お支払いを確認中です",
    paymentPendingBody:
      "お支払いをまだ確認できていません。もう一度お支払いはしないでください。ご予約はお店が確認します。",
    paymentPendingChargedBody:
      "お支払いは完了しましたが、予約の手続きを完了できませんでした。もう一度お支払いはしないでください。ご予約はお店が確認します。",
    close: "閉じる",
    bookingInProgress:
      "ご予約を処理中です。しばらくお待ちください。もう一度お支払いはしないでください。",
    bookingMayExist:
      "ご予約がすでに完了している可能性があります。もう一度お支払いはしないでください。SMSまたはメールの確認のお知らせをご確認いただくか、お店にお問い合わせください。",

    sendRequest: "リクエストを送信",
    allBooked: "予約済み",
    nServices: (n: number) => `${n}件のサービス`,
  },
};

type DeepStringify<T> = {
  [K in keyof T]: T[K] extends (...args: infer A) => infer _R
    ? (...args: A) => string
    : T[K] extends object
      ? DeepStringify<T[K]>
      : string;
};

export type WebDictionary = DeepStringify<(typeof dict)["en"]>;

export function getDictionary(locale: WebLocale): WebDictionary {
  return dict[locale] ?? dict.en;
}
